/**
 * Desktop (`lg` and up) view of the request-a-quote modal: a `Modal` with the quote-name input,
 * one row per shown line (product, quantity, unit and extended price, cut details, per-line
 * inputs), the subtotal and the footer buttons. Holds no request logic: every value and handler
 * comes from `QuoteModal.tsx` through {@link QuoteModalType} (`QuoteHelper.ts`).
 *
 * The footer switches on `containsUncertainPricesOrInactiveItems`, computed here from the
 * Prices API response and the shown products: when a line has a `pricePerUom` of 0 or is
 * inactive or unavailable, the quote-name input is hidden and Request Price and Remove & Reprice
 * replace Save Quote. After a successful Request Price, the body and footer become the result
 * panel ({@link PriceRequestResultButtons}).
 *
 * Rule: "the customer is never shown a price the Prices or Quotes API didn't return"; "the APIs
 * may return a $0 value for price in which case we always show "TBD" to the end user and never
 * "$0"". Line prices go through `QuoteHelper.ts` → `priceOrTbd`; the subtotal shows TBD whenever
 * a line has a 0 price or is inactive. While prices load, the loading rule quoted in
 * `QuoteHelper.ts`'s header applies; the subtotal comment in {@link ButtonArea} says where this
 * view's em dash matches it and where it is a known defect.
 *
 * `QuoteModalMobile.tsx` is the mobile counterpart and imports
 * {@link PriceRequestResultButtons} from here. The two differ in places: see
 * `QuoteModalMobile.tsx`'s header.
 */
import React from "react";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Modal, Popover } from "@ryerson/frontend.modal";
import { useApplication } from "@ryerson/frontend.application";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { Checkbox, ErpInput, Input } from "@ryerson/frontend.form-controls";
import { Alert } from "@ryerson/frontend.notification";
import { addImageDefaultSrc, getProductFormShapeImagePath } from "@components/Helper/common";
import { useTheme } from "@ryerson/frontend.theme";
import { Button } from "@ryerson/frontend.button";
import {
	validateErpRestrictedCharacters,
	emDash,
	ShoppingCartPricingItem,
	CurrencyCode,
	displayPrice,
	navigateToProductDetails,
	getDisplayUom,
	determineProductDisplay,
	LanguageToLocale,
} from "@ryerson/frontend.common";
import { Icon } from "@ryerson/frontend.assets";
import {
	ButtonAreaProps,
	checkIsActiveItem,
	getPricingInformationForItem,
	HasActiveAvailableProps,
	intoErpValidator,
	priceOrTbd,
	ProductRowProps,
	QuoteModalProductTypeKeys,
	QuoteModalType,
	resolveAttributeDisplay,
	UpdatedProduct,
} from "./QuoteHelper";
import DefineCutDisplay from "@components/Shared/DisplayDefineCut/DefineCutDisplay";
import {
	Localization,
	UpdatedProductAttribute,
} from "@components/Shared/ProductDisplay/Helpers/ProductDisplay.models";
import rgba from "polished/lib/color/rgba";
import { QuoteModalContentProps } from "./QuoteModalContent";
import { navigate } from "@ryerson/frontend.next";
import { PriceRequestResult } from "./QuoteModal";
import { withLanguage } from "./Content";
import {
	ProductArea,
	ProductDetailItem,
	ProductDetailSection,
	QuoteName,
} from "./QuoteModal.Styled";

/**
 * One line of the desktop modal: the product (linked to its details page when active), its
 * quantity, unit price and extended price, a "cannot be priced online" warning for a 0 price or
 * an inactive product, the cut and its source attributes when the line has one, and the
 * per-line inputs. Text inputs are `ErpInput`s validated by `validateErpRestrictedCharacters`.
 */
const ProductRow: React.FC<ProductRowProps & { isRetrievingPricing: boolean }> = ({
	catalogProduct,
	currentProductForQuote,
	content,
	pricePerUom,
	extendedPrice,
	quoteInputs,
	pricingUom,
	quantityUom,
	quantity,
	validationInputRef,
	attachValidationRef,
	isRetrievingPricing,
}) => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();
	const [inactiveItem, setItemInactive] = React.useState(false);
	const { gray, lighterGray, selected, background, white, lightGray } = theme.colors.primary;
	const showDefineCut = Object.keys(currentProductForQuote?.processing || {}).length > 0;
	const local = Localization[language];

	const getProductAttributeForDesktop = (productAttribute: UpdatedProductAttribute) => {
		const { name, value } = resolveAttributeDisplay(productAttribute, language, local);
		return (
			<ProductDetailItem>
				<Typography weight="bold" variant="div" css={{ paddingBottom: 4 }}>
					{name}
				</Typography>
				<Typography variant="div">{value}</Typography>
			</ProductDetailItem>
		);
	};
	const locale = LanguageToLocale[language];
	// The inactive flag is set only once the line has a non-zero price, and is not cleared
	// when that later stops being true. A 0-price line shows TBD through `priceOrTbd` anyway.
	React.useEffect(() => {
		if (!!pricePerUom && !isRetrievingPricing && !!catalogProduct) {
			setItemInactive(!checkIsActiveItem(catalogProduct));
		}
	}, [isRetrievingPricing, pricePerUom, catalogProduct]);

	return (
		<>
			<Flex
				alignItems="center"
				justifyContent="space-between"
				css={{ background: selected, color: background, padding: "15px 20px 15px 5px" }}
			>
				<Flex css={{ gap: 18, width: "619px" }} alignItems="center">
					<img
						onError={(event) => addImageDefaultSrc(event)}
						src={getProductFormShapeImagePath(
							catalogProduct?.category,
							catalogProduct?.form,
							catalogProduct?.shape
						)}
						css={{ maxHeight: "100px", maxWidth: "100px" }}
					/>
					<Flex
						justifyContent="flex-start"
						css={{ color: background }}
						wrap="wrap"
						alignItems="flex-start"
						direction={showDefineCut ? "column" : "row"}
					>
						<Typography size="md" color={background}>
							{catalogProduct?.active ? (
								<Link
									type="secondary"
									to={navigateToProductDetails(
										catalogProduct?.mageniumId,
										catalogProduct?.altPartNumber || ""
									)}
									color={white}
									underline={true}
								>
									{determineProductDisplay(
										catalogProduct?.altPartNumber || "",
										catalogProduct?.fastPath
									)}
								</Link>
							) : (
								<Typography color={white}>
									{determineProductDisplay(
										catalogProduct?.altPartNumber || "",
										catalogProduct?.fastPath
									)}
								</Typography>
							)}
						</Typography>
						{showDefineCut ? (
							<Typography weight="bold" color={white} size="md">
								{content.cutApplied}
							</Typography>
						) : (
							<Typography size="md" color={background}>
								&nbsp;
								{catalogProduct?.itemDescriptionLocalization?.[locale]
									?.shortDescription || ""}
							</Typography>
						)}
					</Flex>
				</Flex>
				<Flex direction="column">
					<Flex alignItems="center" css={{ gap: 20 }}>
						<FlexItem css={{ width: "100px" }}>
							<Typography color={background}>
								{quantityUom &&
									`${quantity} ${getDisplayUom(
										catalogProduct?.form,
										quantityUom,
										"quantity"
									)}`}
							</Typography>
						</FlexItem>
						<FlexItem css={{ width: "100px" }}>
							<Typography weight="bold" color={background}>
								{inactiveItem ? content.tbd : priceOrTbd(pricePerUom, content.tbd)}
							</Typography>
							{pricingUom &&
								` / ${getDisplayUom(catalogProduct?.form, pricingUom, "price")}`}
						</FlexItem>
						<FlexItem css={{ width: "100px" }}>
							<Typography weight="bold" color={background}>
								{inactiveItem
									? content.tbd
									: priceOrTbd(extendedPrice, content.tbd)}
							</Typography>
						</FlexItem>
					</Flex>
					{pricePerUom === 0 ? (
						<FlexItem>
							<Flex alignItems="center" css={{ marginTop: "10px" }}>
								<FlexItem css={{ marginRight: "12px" }}>
									<Icon icon="warning" />
								</FlexItem>
								<FlexItem>{content.itemCannotBePriced}</FlexItem>
							</Flex>
						</FlexItem>
					) : (
						""
					)}

					{inactiveItem ? (
						<FlexItem>
							<Flex alignItems="center" css={{ marginTop: "10px" }}>
								<FlexItem css={{ marginRight: "12px" }}>
									<Icon icon="warning" />
								</FlexItem>
								<FlexItem>{content.itemCannotBePriced}</FlexItem>
							</Flex>
						</FlexItem>
					) : (
						""
					)}
				</Flex>
			</Flex>
			<Flex direction="column" css={{ background: lighterGray, padding: "20px 25px 24px" }}>
				{" "}
				{showDefineCut && catalogProduct?.attributes?.length ? (
					<div css={{ maxWidth: 428 }}>
						<Flex
							direction="row"
							css={{ marginBottom: 10, gap: 10 }}
							alignItems="center"
						>
							<Typography weight="bold" size="md">
								{" "}
								{content.cutFrom}:
								<Typography weight="normal">
									{
										catalogProduct?.itemDescriptionLocalization?.[locale]
											?.shortDescription
									}
								</Typography>
							</Typography>
						</Flex>
						{!!catalogProduct?.attributes ? (
							<ProductDetailSection>
								{catalogProduct.attributes.map((attribute, index) => (
									<FlexItem key={index}>
										{getProductAttributeForDesktop(
											attribute as UpdatedProductAttribute
										)}
									</FlexItem>
								))}
							</ProductDetailSection>
						) : null}
					</div>
				) : null}{" "}
				{showDefineCut && (
					<Flex css={{ width: "100%", paddingBottom: "20px" }}>
						<FlexItem css={{ width: "40%" }}>
							<DefineCutDisplay
								defineCutParameters={currentProductForQuote?.processing || {}}
								product={catalogProduct}
								canEditCut={false}
								canDeleteCut={false}
							/>
						</FlexItem>
					</Flex>
				)}
				<Flex css={{ gap: 38 }} justifyContent="flex-start" alignItems="flex-start">
					{quoteInputs?.map((input, index) => {
						return !!input.type &&
							input.type === "checkbox" &&
							input.productTypeKey === QuoteModalProductTypeKeys.domesticRequired ? (
							<Flex
								key={index}
								direction="row"
								css={{
									gap: 10,
									alignSelf: "flex-end",
									alignItems: "center",
								}}
							>
								<Flex css={{ width: 352 }}>
									<Checkbox
										value={input.label}
										checked={currentProductForQuote[input.productTypeKey]}
										onChange={(e, checked) => {
											input.onChange(checked, currentProductForQuote);
										}}
										language={language}
									/>
								</Flex>

								<Popover
									offset={[-225, 10]}
									caretColor={theme.colors.primary.white}
									alignment={"bottom"}
									className={`trigger info-icon-${index}`}
									hover={true}
									trigger={
										<span>
											<Icon
												icon="question-mark"
												size="sm"
												circled={true}
												css={{ backgroundColor: white }}
											/>
										</span>
									}
									dataTestId="domestic-required-help-popover"
								>
									<div
										css={{
											border: `1px solid ${rgba(lightGray, 0.6)}`,
											padding: 20,
											maxWidth: 300,
										}}
									>
										<Typography autoResize={false} size="sm" color={lightGray}>
											{content.domesticToolTip}
										</Typography>
									</div>
								</Popover>
							</Flex>
						) : (
							<Flex
								key={index}
								direction="column"
								css={{ gap: 4, flexGrow: 0, flexShrink: 0 }}
								itemRef={attachValidationRef ? validationInputRef : undefined}
							>
								<Typography color={gray} size="md">
									{input.label}
								</Typography>
								<ErpInput
									onChange={(event) =>
										input.onChange(event, currentProductForQuote)
									}
									value={currentProductForQuote[input.productTypeKey] as string}
									language={language}
									size="sm"
									css={{ width: 154 }}
									validation={validateErpRestrictedCharacters}
								/>
							</Flex>
						);
					})}
				</Flex>
			</Flex>
		</>
	);
};

/**
 * Footer of the desktop modal: the subtotal, then either Save Quote (no line has a 0
 * `pricePerUom` or an inactive product, which includes a line with no price and a failed
 * pricing call) or the "cannot be priced online" disclaimer with Request Price and Remove &
 * Reprice.
 * Shows the failure message after a failed request, and "Processing..." in place of the buttons
 * while pricing is in flight.
 *
 * Request Price is disabled after its first click: `hasRequestedPrice` disables it (and Remove &
 * Reprice and Save Quote) and is reset only when the modal closes, so a failed request cannot
 * be retried until the modal is reopened. `QuoteModal.tsx` → `handleOnClickSaveQuote` does not
 * guard a price request itself, so `hasRequestedPrice` is this view's guard under the
 * single-send rule in `QuoteModal.tsx`'s header.
 * Remove & Reprice is also disabled when every priced line is 0 or inactive, since it would
 * leave nothing.
 */
const ButtonArea: React.FC<ButtonAreaProps> = ({
	content,
	handleOnClickSaveQuote,
	pricingData,
	handleSaveAsList,
	handleRemoveReprice,
	selectedProducts,
	quoteCreateSubmitted,
	priceRequestResult,
	containsUncertainPricesOrInactiveItems,
	isRetrievingPricing,
	showQuoteModal,
}) => {
	const { theme } = useTheme();
	const { label } = theme.colors.primary;

	// Add state to track if request price is being processed
	const [isRequestingPrice, setIsRequestingPrice] = React.useState(false);
	// Disables Request Price, Remove & Reprice and Save Quote after the first Request Price
	// click, until the modal closes
	const [hasRequestedPrice, setHasRequestedPrice] = React.useState(false);

	// Wrapper function to handle request price with loading state
	const handleRequestPrice = React.useCallback(async () => {
		if (isRequestingPrice || isRetrievingPricing || hasRequestedPrice) return; // Prevent multiple clicks

		setIsRequestingPrice(true);
		setHasRequestedPrice(true); // Stays set until the modal closes
		try {
			// `handleSaveAsList` (from `QuoteModal.tsx`) returns nothing, so this does not wait
			// for the request; `isRequestingPrice` clears at once and `hasRequestedPrice` is the
			// guard that holds.
			await handleSaveAsList();
		} catch (error) {
			console.error("Price request failed:", error);
		} finally {
			setIsRequestingPrice(false);
		}
	}, [handleSaveAsList, isRequestingPrice, isRetrievingPricing, hasRequestedPrice]);

	// Reset loading state once a price-request result is shown or no line needs a price request
	React.useEffect(() => {
		if (priceRequestResult.display || !containsUncertainPricesOrInactiveItems) {
			setIsRequestingPrice(false);
			// Don't reset hasRequestedPrice here: it stays set until the modal closes
		}
	}, [priceRequestResult.display, containsUncertainPricesOrInactiveItems]);

	// Reset when modal is closed - reset both states
	React.useEffect(() => {
		if (!showQuoteModal) {
			setIsRequestingPrice(false);
			setHasRequestedPrice(false); // Only reset when modal is completely closed
		}
	}, [showQuoteModal]);

	// mapper to find the selected product that matches the pricing item
	const pricingItemToSelectedProduct = function (item: ShoppingCartPricingItem) {
		return selectedProducts.find((product) => {
			return item.materialSku === product.materialMaster;
		});
	};

	const allItemsUncertainOrInactive =
		!!pricingData?.items?.length &&
		pricingData?.items.every(
			(item) =>
				item.pricePerUom === 0 ||
				!checkIsActiveItem(pricingItemToSelectedProduct(item) as HasActiveAvailableProps)
		);

	// Subtotal: the `{ true: …, [cond.toString()]: … }["true"]` lookups below return the value
	// of the last true condition. The amount shows only when every line is priced and active;
	// an em dash when `pricingData` has no items or a line's `pricePerPound` is not a number;
	// TBD when `containsUncertainPricesOrInactiveItems` is set (a line has a 0 price or is
	// inactive). `QuoteModalMobile.tsx` tests `pricePerUom` for the em dash instead.
	// Against the loading rule in `QuoteHelper.ts`'s header: the em dash before the first
	// response matches it. The em dash after a failed pricing call, or after a response with a
	// line whose `pricePerPound` is not a number, is a known defect. So is TBD before any
	// response in a reopened modal: the footer mode is not reset on close, so it can still be
	// set from the previous opening.
	return (
		<Flex direction="column" css={{ width: "100%" }}>
			<Flex
				justifyContent={
					containsUncertainPricesOrInactiveItems ? "space-between" : "flex-end"
				}
				css={{ marginBottom: "30px" }}
			>
				{!priceRequestResult.display &&
				!priceRequestResult.success &&
				!isRetrievingPricing &&
				containsUncertainPricesOrInactiveItems ? (
					<FlexItem css={{ maxWidth: "65%" }}>
						<Flex>
							<FlexItem>
								<Icon icon="warning" size="md" />
							</FlexItem>
							<FlexItem>
								<Typography
									color={label}
									css={{ marginLeft: "12px", display: "block" }}
								>
									{content.disclaimer}
									<br />
									<br />
									{content.disclaimer2}
								</Typography>
							</FlexItem>
						</Flex>
					</FlexItem>
				) : (
					<></>
				)}
				{priceRequestResult.display && !priceRequestResult.success ? (
					<Flex alignItems="center" css={{ width: "100%" }}>
						<FlexItem>
							<Icon icon="error" size="md" />
						</FlexItem>
						<FlexItem>
							<Typography
								color={label}
								css={{ marginLeft: "12px", display: "block" }}
							>
								{content.failPriceRequest}
								<Link next={false} to={`mailTo:${content.brandedEmail}`}>
									{content.brandedEmail}
								</Link>
							</Typography>
						</FlexItem>
					</Flex>
				) : (
					<></>
				)}
				<Flex direction="column" css={{ width: "100%" }}>
					<Flex justifyContent="flex-end">
						<Typography color={label} size="md">
							{content.subtotal}
						</Typography>
					</Flex>
					<Flex direction="row" alignItems="center" justifyContent="flex-end">
						<Typography
							color={label}
							variant="p"
							weight="bold"
							css={{ paddingTop: "8px" }}
						>
							{!containsUncertainPricesOrInactiveItems && pricingData.currency}
						</Typography>
						&nbsp;
						<Typography
							color={label}
							variant="h3"
							weight="bold"
							css={{ paddingTop: "8px" }}
						>
							{
								{
									true: `${
										displayPrice(Number(pricingData.subtotal)).split(".")[0]
									}`,
									[(
										!pricingData?.items?.length ||
										pricingData.items.some((item) =>
											Number.isNaN(Number(item.pricePerPound))
										)
									).toString()]: emDash,
									[containsUncertainPricesOrInactiveItems.toString()]:
										content.tbd,
								}["true"]
							}
						</Typography>
						<Typography color={label} variant="p" weight="bold">
							{
								{
									true: `.${
										Number(pricingData.subtotal).toFixed(2).split(".")[1]
									}`,
									[(
										!pricingData?.items?.length ||
										pricingData.items.some((item) =>
											Number.isNaN(Number(item.pricePerPound))
										)
									).toString()]: "",
									[containsUncertainPricesOrInactiveItems.toString()]: "",
								}["true"]
							}
						</Typography>
					</Flex>
				</Flex>
			</Flex>
			{containsUncertainPricesOrInactiveItems ? (
				<Flex>
					{!isRetrievingPricing ? (
						<>
							<Button
								label={content.requestPrice}
								onClick={handleRequestPrice}
								disabled={
									hasRequestedPrice || isRequestingPrice || isRetrievingPricing
								}
								size="lg"
								dataTestId="quote-add-to-shopping-list"
							/>
							<Button
								label={content.removeAndReprice}
								onClick={handleRemoveReprice}
								disabled={
									allItemsUncertainOrInactive ||
									hasRequestedPrice ||
									isRequestingPrice ||
									isRetrievingPricing
								}
								size="lg"
								css={{
									marginLeft: "20px",
								}}
								type="secondary"
								dataTestId="quote-remove-and-reprice"
							/>
						</>
					) : (
						<Typography size="md" color={label}>
							{content.processingRequest}
						</Typography>
					)}
				</Flex>
			) : (
				<Flex css={{ gap: 36 }} alignItems="center">
					{!isRetrievingPricing ? (
						<>
							<Button
								label={content.saveQuote}
								disabled={
									quoteCreateSubmitted || isRequestingPrice || hasRequestedPrice
								}
								onClick={() => handleOnClickSaveQuote()}
								fullwidth={true}
								size="lg"
								css={{ width: 183 }}
								dataTestId="save-as-quote"
							/>
							<Typography size="sm" color={label}>
								{content.clickQuotes}
							</Typography>
						</>
					) : (
						<Typography size="md" color={label}>
							{content.processingRequest}
						</Typography>
					)}
				</Flex>
			)}
		</Flex>
	);
};

/**
 * Buttons of the price-request result panel, shared with `QuoteModalMobile.tsx`: Finish clears
 * the result and closes the modal; View Details opens the new Price Request quote at
 * `/dashboard/quotes/<id>`, or the quotes list when the response carried no quote. Renders
 * nothing unless the request succeeded.
 */
export const PriceRequestResultButtons: React.FC<{
	priceRequestResult: PriceRequestResult;
	setPriceRequestResult: React.Dispatch<React.SetStateAction<PriceRequestResult>>;
	content: QuoteModalContentProps;
	isMobile?: boolean;
	setShowQuoteModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
	priceRequestResult,
	setPriceRequestResult,
	content,
	isMobile = false,
	setShowQuoteModal,
}) => {
	return (
		<>
			{priceRequestResult.success ? (
				<>
					<Flex
						css={{ width: "100%", gap: "20px" }}
						direction={isMobile ? "column" : "row"}
					>
						<Button
							label={content.finish}
							onClick={() => {
								setPriceRequestResult({
									success: false,
									display: false,
								});
								setShowQuoteModal(false);
							}}
							size="lg"
							dataTestId="quote-modal-close-price-req-success"
						/>
						<Button
							label={content.viewDetails}
							onClick={() => {
								if (priceRequestResult.quote) {
									navigate(`/dashboard/quotes/${priceRequestResult?.quote.id}`);
								} else {
									navigate(`/dashboard/quotes/`);
								}
							}}
							size="lg"
							type="secondary"
							dataTestId="quote-modal-view-details-price-req-success"
						/>
					</Flex>
				</>
			) : (
				<></>
			)}
		</>
	);
};

/** The desktop modal (see the file header). Rendered by `QuoteModal.tsx` at `lg` and up. */
const QuoteModalDesktop: React.FC<QuoteModalType> = ({
	quoteName,
	setQuoteName,
	selectedProducts,
	showQuoteModal,
	setShowQuoteModal,
	content,
	quoteProductList,
	handleOnClickSaveQuote,
	pricingData,
	handleSaveAsList,
	handleRemoveReprice,
	quoteInputs,
	submitted,
	quoteCreateSubmitted,
	validationInputRef,
	priceRequestResult,
	setPriceRequestResult,
	isRetrievingPricing,
}) => {
	const {
		localization: { language },
	} = useApplication();
	const quoteModalContent = withLanguage[language];
	const [hasMounted, setHasMounted] = React.useState(false);
	const { theme } = useTheme();
	const { lighterGray } = theme.colors.primary;
	const [containsUncertainPricesOrInactiveItems, setContainsUncertainPricesOrInactiveItems] =
		React.useState(false);
	const [showAlert, setShowAlert] = React.useState(false);

	// Decides the footer mode once pricing has returned: true when a shown product is inactive
	// or unavailable or a priced line has `pricePerUom` 0. It is never reset, so a reopened
	// modal starts in the previous opening's mode until its own pricing returns.
	React.useEffect(() => {
		if (
			hasMounted &&
			showQuoteModal &&
			pricingData?.items &&
			selectedProducts.length &&
			!isRetrievingPricing
		) {
			const hasInactiveOrUnavailableItems = (
				selectedProducts as HasActiveAvailableProps[]
			).some((item) => !checkIsActiveItem(item));
			const hasUncertainPrices = (pricingData?.items || []).some(
				(item: ShoppingCartPricingItem) => item.pricePerUom === 0
			);

			setContainsUncertainPricesOrInactiveItems(
				() => hasUncertainPrices || hasInactiveOrUnavailableItems
			);
		}
	}, [hasMounted, pricingData, selectedProducts, showQuoteModal]);

	React.useEffect(() => {
		setHasMounted(true);
	}, []);

	React.useEffect(() => {
		if (!showQuoteModal) {
			setShowAlert(false);
		}
	}, [showQuoteModal]);

	// Save Quote. `ButtonArea` calls this with no argument, so `handleOnClickSaveQuote` receives
	// `undefined` and treats it as a Save Quote, not a price request. A missing name also
	// scrolls to the top and shows the `Content.tsx` alert.
	const handleOnSaveQuote = (event: any) => {
		setShowAlert(false);
		handleOnClickSaveQuote(event);
		if (!quoteName) {
			window.document.getElementById("quote-top")?.scrollIntoView({ behavior: "smooth" });
			setShowAlert(true);
		}
	};

	return (
		<ContentSection vPadding="0" hPadding="0">
			<Modal
				title={
					!(priceRequestResult.display && priceRequestResult.success)
						? content.saveAsQuote
						: content.priceRequest
				}
				dataTestId="save-as-quote-modal"
				isOpen={showQuoteModal}
				type="primary"
				modalStyle="default"
				closable={true}
				buttonArea={
					<>
						{priceRequestResult.display && priceRequestResult.success ? (
							<PriceRequestResultButtons
								priceRequestResult={priceRequestResult}
								setPriceRequestResult={setPriceRequestResult}
								content={content}
								setShowQuoteModal={setShowQuoteModal}
							/>
						) : (
							<ButtonArea
								content={content}
								handleOnClickSaveQuote={handleOnSaveQuote}
								pricingData={pricingData}
								handleSaveAsList={handleSaveAsList}
								handleRemoveReprice={handleRemoveReprice}
								selectedProducts={selectedProducts}
								quoteCreateSubmitted={quoteCreateSubmitted}
								priceRequestResult={priceRequestResult}
								containsUncertainPricesOrInactiveItems={
									containsUncertainPricesOrInactiveItems
								}
								isRetrievingPricing={isRetrievingPricing}
								showQuoteModal={showQuoteModal}
							/>
						)}
					</>
				}
				onClose={() => setShowQuoteModal(false)}
			>
				<Flex direction="column">
					<span id="quote-top" />
					{showAlert && (
						<>
							<Alert severity="error" size="md">
								<Typography>{quoteModalContent.errorMessage}</Typography>
							</Alert>
						</>
					)}
					{priceRequestResult.display && priceRequestResult.success ? (
						<>
							<Flex direction="column" css={{ gap: 20, marginBottom: 40 }}>
								<Typography size="lg" weight="bold" css={{ width: 619 }}>
									{content.successPriceRequest}
								</Typography>
								<br />
								<Typography size="md" css={{ width: 619 }}>
									{content.reviewOrReturn}
								</Typography>
							</Flex>
						</>
					) : (
						<Flex direction="column">
							{!containsUncertainPricesOrInactiveItems && !isRetrievingPricing ? (
								<Flex css={{ marginBottom: 28 }}>
									<QuoteName
										size="sm"
										maxLength={15}
										value={quoteName}
										label={content.quoteName}
										required={true}
										language={language}
										onChange={(event) => setQuoteName(event.target.value)}
										isValid={
											submitted
												? Boolean(
														quoteName &&
														!validateErpRestrictedCharacters(quoteName)
													)
												: true
										}
										submitted={submitted}
										validation={validateErpRestrictedCharacters}
									/>
								</Flex>
							) : (
								<></>
							)}
							<ProductArea>
								<Flex
									justifyContent="space-between"
									alignItems="center"
									css={{
										background: lighterGray,
										padding: "21px 25px 19px",
										gap: 20,
									}}
								>
									<Flex css={{ width: 619 }}>
										<Typography size="sm" weight="bold">
											{content.product}
										</Typography>
									</Flex>
									<Flex justifyContent="flex-end" css={{ gap: 20 }}>
										<Flex css={{ width: "100px" }}>
											<Typography size="sm" weight="bold">
												{content.quantity}
											</Typography>
										</Flex>
										<Flex css={{ width: "100px" }}>
											<Typography size="sm" weight="bold">
												{`${
													pricingData.currency
														? pricingData.currency
														: CurrencyCode.us
												} ${content.price}`}
											</Typography>
										</Flex>
										<Flex css={{ width: "100px" }}>
											<Typography size="sm" weight="bold">
												{`${
													pricingData.currency
														? pricingData.currency
														: CurrencyCode.us
												} ${content.extendedPrice}`}
											</Typography>
										</Flex>
									</Flex>
								</Flex>
								{quoteProductList.map((currentProductForQuote, productIndex) => {
									/**
									 * We can’t identify items using indices here.
									 * That’s because {@link selectedProducts} isn’t a fixed-sized list.
									 * Its indices and items change when the user hits `remove & reprice`.
									 * Let’s use ids instead.
									 */
									let currentProduct = selectedProducts.find((product) => {
										return (
											currentProductForQuote.materialSku ===
												product.materialMaster &&
											currentProductForQuote.id === product.id
										);
									});

									if (currentProduct) {
										const { pricePerUom, extendedPrice, itemPricingData } =
											getPricingInformationForItem(
												productIndex,
												currentProductForQuote,
												pricingData,
												currentProduct
											);

										// `attachValidationRef`: `intoErpValidator` returns
										// an object, so this `findIndex` is 0 for any list
										// and the ref goes to the first row, invalid or not.
										return (
											<React.Fragment key={productIndex}>
												<ProductRow
													isRetrievingPricing={isRetrievingPricing}
													catalogProduct={
														currentProduct as UpdatedProduct
													}
													currentProductForQuote={currentProductForQuote}
													content={content}
													pricePerUom={pricePerUom}
													extendedPrice={extendedPrice}
													key={productIndex}
													quoteInputs={quoteInputs}
													pricingUom={itemPricingData?.pricingUom || ""}
													quantityUom={itemPricingData?.quantityUom || ""}
													quantity={itemPricingData?.quantity || ""}
													attachValidationRef={
														quoteProductList.findIndex(
															intoErpValidator
														) === productIndex
													}
													validationInputRef={validationInputRef}
												/>
											</React.Fragment>
										);
									} else {
										return <React.Fragment key={productIndex}></React.Fragment>;
									}
								})}
							</ProductArea>
						</Flex>
					)}
				</Flex>
			</Modal>
		</ContentSection>
	);
};

export default QuoteModalDesktop;
